import React from "react";

import {
    AdjustmentsVerticalIcon,
    CheckCircleIcon,
    PlusIcon,
    TableCellsIcon,
} from "@heroicons/react/24/outline";
import {useFormSet} from "reactivated/dist/forms";

import * as habits from "@client/features/habits";

import * as constants from "@client/constants";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {Image} from "@client/components/Image";
import {ManagerLayout} from "@client/site/ManagerLayout";
import {useMessages} from "@client/site/context";
import * as forms from "@client/site/forms";
import {classNames, reverse, server} from "@reactivated";

const styles = {
    grid: "grid gap-[15px] auto-rows-fr grid-cols-[repeat(var(--grid-columns),1fr)] max-sm:grid-cols-1",
    clientNavigation:
        "client-navigation flex items-center justify-between flex-wrap gap-2.5 [&>*]:shrink",
    clientNavigationTabs: "",
};

const dashboardStyles = {
    empty: "client-dashboard-empty border-2 border-dashed border-gray-300 rounded-lg flex flex-col justify-center items-center py-[4vh]",
};

export const Grid = (props: {columns: number; children: React.ReactNode}) => (
    <div
        style={{"--grid-columns": props.columns} as React.CSSProperties}
        className={styles.grid}
    >
        {props.children}
    </div>
);

export const ClientNavigation = ({
    children,
    tabs,
}: {
    children: React.ReactNode;
    tabs?: [string, string];
}) => (
    <>
        <div className={styles.clientNavigation}>{children}</div>
        {tabs != null && <div className={styles.clientNavigationTabs}>Coming soon</div>}
    </>
);

const icons = {
    BASIC: {
        iconColor: "bg-purple-500",
        icon: TableCellsIcon,
    },
    LOG: {
        iconColor: "bg-pink-500",
        icon: CheckCircleIcon,
    },
    ADVANCED: {
        iconColor: "bg-yellow-500",
        icon: AdjustmentsVerticalIcon,
    },
};

const Targets = (props: server.manager.templates.ClientManagerPlanner) => {
    return (
        <ul role="list" className="grid grid-cols-2 gap-4">
            {props.targets.map((target) => {
                const Icon = icons[target.type];

                return (
                    <li
                        key={target.id}
                        className="
                            relative rounded-lg border border-gray-300 bg-white
                            shadow-xs
                            hover:bg-indigo-100/20
                        "
                    >
                        <div
                            className="
                                flex items-center gap-x-4 border-b border-gray-900/5 p-4
                            "
                        >
                            <Icon.icon
                                className={classNames(
                                    "p-2 h-10 w-10 flex-none rounded-lg object-cover text-white",
                                    Icon.iconColor,
                                )}
                            />
                            <div className="text-base/6 font-medium text-gray-900">
                                {target.name}
                            </div>
                        </div>
                        <dl
                            className="
                                -my-3 divide-y divide-gray-100 px-4 py-3 text-sm/6
                            "
                        >
                            {target.meal_plan != null && (
                                <div className="flex justify-between gap-x-4 py-3">
                                    <dt className="font-medium text-gray-900">
                                        Meal plan
                                    </dt>
                                    <dd className="text-gray-700">
                                        {target.meal_plan.name}
                                    </dd>
                                </div>
                            )}
                            {constants.ALL_METRICS.map(([metric, , , unit, label]) => {
                                const value = target[`${metric}_target`];
                                const mode = target[`${metric}_mode`];
                                const modeLabel =
                                    mode == "EQUAL"
                                        ? ""
                                        : server.core.models.TargetLike.Mode[mode];

                                if (value == null) {
                                    return null;
                                }

                                return (
                                    <div
                                        key={metric}
                                        className="flex justify-between gap-x-4 py-3"
                                    >
                                        <dt className="font-medium text-gray-900">
                                            {label}
                                        </dt>
                                        <dd className="text-gray-700">
                                            {modeLabel}{" "}
                                            {utils.formatDecimalAndUnit(
                                                value,
                                                unit,
                                                props.client_manager.client.profile
                                                    .is_imperial,
                                            )}
                                        </dd>
                                    </div>
                                );
                            })}
                        </dl>
                        <a
                            className="absolute inset-0"
                            href={reverse("client_planner_targets", {
                                client_manager_id: props.client_manager.id,
                                target_id: target.id,
                            })}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

export const Template = (props: server.manager.templates.ClientManagerPlanner) => {
    const messages = useMessages();
    const targetAssignmentFormSet = useFormSet({
        formSet: props.target_assignment_form_set,

        changeInterceptor: (fieldName, prevValues, nextValues) => {
            const nextType = nextValues.check_in;

            if (
                fieldName == "check_in" &&
                nextType === "ENABLED" &&
                props.can_schedule_check_in === false
            ) {
                messages.info(
                    "To enable check-ins you must first choose your check-in questions. You can find these on the Dashboard under Settings",
                );
                return prevValues;
            }

            return nextValues;
        },
    });

    const addTarget = (
        <typography.Button
            href={reverse("client_planner_targets_create", {
                client_manager_id: props.client_manager.id,
            })}
        >
            <PlusIcon className="mr-1.5 -ml-0.5 size-5" aria-hidden="true" />
            New Target
        </typography.Button>
    );

    return (
        <ManagerLayout
            title="Planner"
            currentClientManagerId={props.client_manager.id}
            narrow="liquid"
        >
            <form noValidate method="POST" action="">
                <forms.CSRFToken />
                <forms.ManagementForm formSet={targetAssignmentFormSet.schema} />

                <habits.ClientHabits
                    targets={props.targets}
                    can_schedule_check_in={props.can_schedule_check_in}
                    client_manager={props.client_manager}
                    planner={props.planner}
                />

                <div
                    className="
                        mt-4 flex flex-col-reverse gap-4
                        md:flex-row
                        xl:gap-8
                    "
                >
                    <div
                        className="
                            flex flex-1 flex-col
                            lg:max-xl:basis-5/12
                        "
                    >
                        <div className="mb-2 flex items-center">
                            <h1 className="flex-1 text-2xl/6 text-gray-900">
                                Macro targets
                            </h1>
                            <div className="">{addTarget}</div>
                        </div>

                        {props.targets.length > 0 ? (
                            <Targets {...props} />
                        ) : (
                            <div
                                className={classNames(dashboardStyles.empty, "h-full")}
                            >
                                <Image image="empty-target" title="Bullseye" />
                                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                                    This client has no targets.
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Add a target to get started.
                                </p>
                                <div className="mt-4">{addTarget}</div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-6 flex flex-col gap-4">
                    <h1 className="flex-1 text-2xl/6 text-gray-900">Settings</h1>
                    <typography.P>
                        When you hide calories, nutrients and body measurements, your
                        client will only see food names and portions.
                    </typography.P>
                    <forms.Fields
                        form={props.settings_form}
                        fields={["has_hidden_macros"]}
                    />
                    <typography.ButtonGroup>
                        <typography.Button submit>Submit</typography.Button>
                        <typography.Button submit name="action" value="clear">
                            Reset
                        </typography.Button>
                    </typography.ButtonGroup>
                </div>
            </form>
        </ManagerLayout>
    );
};
